import React from 'react';
import styled from 'styled-components';
import { Flex, Typo, theme } from '../../../../ui';
import Icon from '../../../../ui/Icon';
import { useSettingsObject } from '../../../settings/hooks';
import { getUserThemeColor, toRgba } from '../../../../ui/theme/colors';

const WidgetContainer = styled(Flex)`
  background: ${theme.colors.NEU_0};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px ${toRgba(theme.colors.NEU_999, 0.04)};
  width: 100%;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${theme.colors.NEU_100};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const IconCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => props.$bgColor}15;
  flex-shrink: 0;
`;

const getActivityColor = (type) => {
  switch (type) {
    case 'submission':
      return theme.colors.PRIMARY;
    case 'quiz_completed':
      return theme.colors.SUCCESS_GREEN;
    case 'discussion':
      return theme.colors.MARKER;
    case 'enrollment':
      return theme.colors.PRIMARY;
    case 'completion':
      return theme.colors.SUCCESS_GREEN;
    default:
      return theme.colors.NEU_600;
  }
};

/**
 * StudentActivityWidget Component
 *
 * Shows recent student activity in teacher's courses:
 * - Submissions
 * - Quiz completions
 * - Discussion posts
 * - Enrollments
 * - Module completions
 */
const StudentActivityWidget = ({ activities = [] }) => {
  const settings = useSettingsObject();
  const userTheme = settings?.theme;
  const brandColor = userTheme?.color ? getUserThemeColor(userTheme?.color) : theme.colors.PRIMARY;

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  if (!activities.length) {
    return null;
  }

  return (
    <WidgetContainer direction='column' align='flex-start'>
      <Flex align='center' justify='space-between' width='100%' style={{ marginBottom: '20px' }}>
        <Flex direction='column' align='flex-start'>
          <Typo.ContentSmallHeader $textAlign='left' color={theme.colors.NEU_PRIMARY}>
            Recent Student Activity
          </Typo.ContentSmallHeader>
          <Typo.Info color={theme.colors.NEU_600}>What your students have been up to</Typo.Info>
        </Flex>
        <Icon.Insights size={'medium'} color={brandColor} />
      </Flex>

      <ActivityList>
        {activities.slice(0, 5).map((activity) => {
          const ActivityIcon = Icon[activity.icon] || Icon.Student;
          const activityColor = getActivityColor(activity.type);

          return (
            <ActivityItem key={activity.id}>
              <IconCircle $bgColor={activityColor}>
                <ActivityIcon size={'small'} color={activityColor} />
              </IconCircle>
              <Flex direction='column' align='flex-start' style={{ flex: 1 }}>
                <Flex align='center' gap='8' style={{ marginBottom: '4px' }}>
                  <Typo.TextTitle fontWeight='600'>{activity.studentName}</Typo.TextTitle>
                  <Typo.Info color={theme.colors.NEU_300}>•</Typo.Info>
                  <Typo.Info color={theme.colors.NEU_600}>{formatRelativeTime(activity.timestamp)}</Typo.Info>
                </Flex>
                <Typo.SmallText color={theme.colors.NEU_600}>{activity.action}</Typo.SmallText>
                <Typo.Info color={theme.colors.NEU_600} style={{ marginTop: '2px' }}>
                  {activity.courseTitle}
                </Typo.Info>
              </Flex>
            </ActivityItem>
          );
        })}
      </ActivityList>
    </WidgetContainer>
  );
};

export default StudentActivityWidget;
